import {
  CANOPY_POST_AUTH_CALLBACK_TARGETS,
  HANDOFF_NONCE_PARAM,
  canopyWebOrigins,
} from '@/utils/constants';

/**
 * The address with the nonce removed, or null when the value is not of the permitted shape.
 *
 * Entirely string work, and deliberately so — this mirrors `baseAddressWithoutNonce` in
 * vercel-auth-functions `lib/redirectAllowlist.ts`, which is the same decision for the same
 * reason. The base is sliced from the original and matched byte-exact against a two-item list,
 * so parsing could never change the answer: credentials, a trailing slash, or an uppercase
 * scheme all fail the base match on their own.
 *
 * Parsing also *loosened* it, because `URLSearchParams` normalises. It reads `?%68%6e=A` as the
 * key `hn` and treats a bare trailing `&` or `#` as nothing, so three caller-controlled byte
 * sequences passed an apparently exact check. Raw string checks refuse them.
 *
 * Host case matters here too: a deep link's host picks the app's screen, and
 * `new URL('sleepio://authCallback').host` is `authcallback`, which matches no route. Our list
 * is all kebab-case today, but a mixed-case handoff route added later depends on the match
 * staying byte-exact. Do not normalise this.
 */
const baseAddressWithoutNonce = (trimmed: string): string | null => {
  // Checked on the raw string or not at all: `new URL` reports a bare trailing `#` as no
  // fragment, and a fragment is not part of an address we honour.
  if (trimmed.includes('#')) {
    return null;
  }

  const queryIdx = trimmed.indexOf('?');
  if (queryIdx === -1) {
    return trimmed;
  }

  const query = trimmed.slice(queryIdx + 1);
  // Exactly `hn=<value>`: one key, spelled literally, non-empty, with nothing that could
  // introduce a second parameter.
  if (!new RegExp(`^${HANDOFF_NONCE_PARAM}=[^&#?]+$`).test(query)) {
    return null;
  }

  return trimmed.slice(0, queryIdx);
};

/**
 * Does this nonce-stripped base sit on one of Canopy's own web origins?
 *
 * String work only, same as everywhere else in this file — an origin from `canopyWebOrigins()`
 * has no trailing slash, so a match is either the origin by itself or the origin followed by a
 * `/`. Deliberately not a full-address match: CAP checks the origin and leaves the path to
 * Canopy, which is the requesting app and the one that knows which screen it wants back.
 */
const matchesCanopyWebOrigin = (base: string): boolean => canopyWebOrigins()
  .some((origin) => base === origin || base.startsWith(`${origin}/`));

/**
 * The one check both `isAllowedAppHandoffTarget` and `isVafAcceptableRedirectTarget` run: native
 * matches byte-exact, web matches by origin.
 *
 * Extracted so the two named predicates below share one body instead of carrying byte-identical
 * copies — a copied body has to diverge by hand, and nothing catches accidental drift meanwhile.
 * They stay separate *functions* because they answer different questions, configured in
 * different places (see each one's own doc comment); today they happen to agree on how the
 * comparison itself works, and that agreement lives here, in the one place both call.
 */
const isAllowedHandoffBase = (base: string): boolean => (
  (CANOPY_POST_AUTH_CALLBACK_TARGETS as readonly string[]).includes(base)
  || matchesCanopyWebOrigin(base)
);

/**
 * Is this exactly one of the addresses a finished Sleepio session may be handed to?
 *
 * Use this — not `isSafeRedirectUri` — anywhere a value decides where a **credential** goes:
 * the address relayed to the auth service, and the address the exchange token is appended to.
 *
 * `isSafeRedirectUri` below checks the scheme only, which is the right check for threading a
 * value through our own navigation but far too loose for delivering a session:
 * `sleepio://anything?x=1` passes it, and `spark://…` passes it too, which would steer a
 * Sleepio session at a different app on the device. This one requires a full match against
 * the native list in constants, or a Canopy web origin, the same way the auth service does.
 *
 * Structure deliberately mirrors `isAllowedRedirectTarget` in vercel-auth-functions: strip the
 * optional nonce off the raw string, then match what is left byte-exact. Neither repo parses the
 * value — see `baseAddressWithoutNonce` above for why a parse cannot tighten this and does
 * loosen it.
 *
 * The address may carry **at most one non-empty `hn`** — the app's per-flow handoff nonce (see
 * `HANDOFF_NONCE_PARAM`). Everything else is still exact, so the destination stays pinned to the
 * two URIs in the list: the relaxation is the query, not the address. `sleepio://evil?hn=A` and
 * `spark://exchange-callback?hn=A` are refused exactly as before.
 */
export const isAllowedAppHandoffTarget = (uri: string | null | undefined): boolean => {
  if (!uri) {
    return false;
  }
  const trimmed = uri.trim();
  if (!trimmed) {
    return false;
  }
  const base = baseAddressWithoutNonce(trimmed);
  if (base === null) {
    return false;
  }
  return isAllowedHandoffBase(base);
};

/**
 * Returns true only for native deep link URIs (e.g. spark://, sleepio://).
 * Uses an allowlist to prevent open redirect attacks.
 *
 * **Scheme-level only.** Fine for threading a redirect target across our own navigation
 * steps, and it is what the Spark flows use. NOT sufficient to decide where a session or a
 * one-time token is delivered — use `isAllowedAppHandoffTarget` above for that.
 */
export const isSafeRedirectUri = (uri: string): boolean => {
  try {
    const url = new URL(uri);
    if (url.username || url.password) {
      return false;
    }
    const allowed = new Set(['spark:', 'sleepio:', 'sleep-companion:']);
    return allowed.has(url.protocol);
  } catch {
    return false;
  }
};

/**
 * May this address be carried from one of our own screens to the next?
 *
 * Threading is not delivery. This only decides whether a value survives a navigation inside
 * CAP; what may actually *receive* a session is still `isAllowedAppHandoffTarget`, which every
 * handoff re-runs against the address at the point the credential is appended. So this is the
 * looser of the two on purpose, and being on this list gets an address no closer to a token.
 *
 * A Canopy web address has to pass here or it never reaches the screen that would check it.
 * The scheme test alone cannot admit one: it accepts a protocol, and web's protocol is https,
 * which would admit the entire internet. So web comes in by origin match instead, through the
 * same allowlist that gates delivery.
 *
 * Deliberately NOT folded into `isSafeRedirectUri`. That function also gates
 * `buildMobileRedirectUrl`, which writes `token` and `refresh_token` into a query string —
 * making web addresses eligible there would put session credentials in a URL. The scheme
 * check stays exactly as narrow as it is; this is a separate, additive question.
 */
export const isThreadableRedirectTarget = (uri: string | null | undefined): boolean => {
  if (!uri) {
    return false;
  }
  return isSafeRedirectUri(uri) || isAllowedAppHandoffTarget(uri);
};

/**
 * Will the auth service accept this address if we relay it?
 *
 * Both lists now, native and Canopy web. This used to be the native list alone, because the
 * auth service had only ever been told about the native addresses and relaying anything else
 * got the whole request rejected — which surfaced to the member as "something went wrong" on
 * the screen where they had just typed their email. It has since learned the web address too,
 * from its own `CANOPY_WEB_ORIGIN` setting, so the answer is the same question
 * `isAllowedAppHandoffTarget` asks and this is a thin alias for it.
 *
 * WHY THE SEPARATE NAME SURVIVES. The two checks agree today, but they answer different
 * questions and are configured in different places: this one is "will the auth service take
 * it", set by that service's environment, while `isAllowedAppHandoffTarget` is "may CAP hand a
 * session here", set by CAP's own. An environment that configures one and not the other makes
 * them disagree, and the call sites need to keep saying which of the two they meant.
 *
 * WHAT A FALSE ANSWER COSTS, still. The address is simply not relayed, and the send succeeds
 * without one. The member's sign-in still completes in the tab they started in, because CAP
 * holds the address in session storage. What breaks is a magic link opened somewhere else —
 * a fresh tab has no session storage, so with no address in the email there is nothing to
 * come back to, and the member lands on the manual handoff screen. That is exactly the case
 * the auth service's web setting exists to fix.
 */
export const isVafAcceptableRedirectTarget = (uri: string | null | undefined): boolean => {
  if (!uri) {
    return false;
  }
  const base = baseAddressWithoutNonce(uri.trim());
  if (base === null) {
    return false;
  }
  return isAllowedHandoffBase(base);
};

/**
 * Builds "?redirectTo=<encoded>" for an address that may be threaded, else "".
 * Always validates so it is safe to call with raw URL input. Used to thread
 * the redirect target across activation step navigations.
 */
export const buildRedirectParam = (redirectTo: string | null | undefined): string => {
  if (!redirectTo || !isThreadableRedirectTarget(redirectTo)) {
    return '';
  }
  return `?redirectTo=${encodeURIComponent(redirectTo)}`;
};

// Route and _p param are the contract the app registers in AppNavigation's linking config (EmailVerification).
const APP_DEEP_LINK_ROUTES: Record<string, string> = {
  sleepio: 'sleepio://emailVerification',
};

export const buildAppDeepLink = (
  product: string | null | undefined,
  currentUrl: string,
): string | null => {
  const route = product ? APP_DEEP_LINK_ROUTES[product] : undefined;
  if (!route) {
    return null;
  }
  return `${route}?_p=${encodeURIComponent(currentUrl)}`;
};

export type MobileAuthTokens = {
  token: string;
  refreshToken?: string;
  userId?: string;
  userEmail?: string;
};

/**
 * Builds the deep link that hands a completed login to the mobile app.
 * Reconstructs the URL from validated components to break taint flow for CodeQL.
 */
export const buildMobileRedirectUrl = (redirectUri: string, tokens: MobileAuthTokens): string | null => {
  if (!isSafeRedirectUri(redirectUri)) {
    return null;
  }
  const queryParams = new URLSearchParams({
    token: tokens.token,
    ...(tokens.refreshToken && { refresh_token: tokens.refreshToken }),
    ...(tokens.userId && { user_id: tokens.userId }),
    ...(tokens.userEmail && { user_email: tokens.userEmail }),
  });
  const validatedUrl = new URL(redirectUri);
  const safeUrl = `${validatedUrl.protocol}//${validatedUrl.host}${validatedUrl.pathname}`;
  return `${safeUrl}?${queryParams.toString()}`;
};
